import React, { useState } from "react";
import { toast } from "sonner";
import { GraduationCap, School, UserRound, Users } from "lucide-react";
import { switchDemoView } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Which role's home route to land on after switching, plus the label/icon
// for its pill — kept as one table so adding a 5th demo slot later is a
// one-line change here, not a scattered one.
const VIEWS = [
    { target: "normal", label: "Member", icon: UserRound, home: "/dashboard" },
    { target: "student", label: "Student", icon: Users, home: "/dashboard" },
    { target: "teacher", label: "Teacher", icon: GraduationCap, home: "/teacher" },
    { target: "principal", label: "Principal", icon: School, home: "/principal" },
];

// Small floating strip, visible only while signed in as any account that
// belongs to the seeded sales-demo group (see seed_demo_group in
// server.py) — a real guardian's `user.demo_group_id` is always absent,
// so this renders nothing for them. Lets a salesperson hop between the
// member/student/teacher/principal views from one login instead of
// signing out and back in four times.
export default function DemoViewSwitcher() {
    const { user, adopt } = useAuth();
    const [switching, setSwitching] = useState(null); // target being switched to, or null

    if (!user?.demo_group_id) return null;

    const handleSwitch = async (view) => {
        if (switching || user.demo_slot === view.target) return;
        setSwitching(view.target);
        try {
            const data = await switchDemoView(view.target);
            adopt(data);
            // Full reload (not a client-side navigate) — some switches land
            // on the SAME route (e.g. Member -> Student both use
            // /dashboard), which react-router won't remount on its own,
            // leaving every page's own mount-effect data fetch stale under
            // the new identity.
            window.location.href = view.home;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't switch that view — try again.");
            setSwitching(null);
        }
    };

    return (
        <div
            className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 rounded-full bg-white/95 backdrop-blur border border-honeySoft shadow-xl shadow-black/10 px-1.5 py-1.5"
            data-testid="demo-view-switcher"
        >
            <span className="pl-2 pr-1 text-[10px] font-display font-bold uppercase tracking-wide text-mocha hidden sm:inline">
                Demo
            </span>
            {VIEWS.map((view) => {
                const Icon = view.icon;
                const active = user.demo_slot === view.target;
                return (
                    <button
                        key={view.target}
                        type="button"
                        onClick={() => handleSwitch(view)}
                        disabled={!!switching}
                        aria-pressed={active}
                        data-testid={`demo-view-switch-${view.target}`}
                        className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-display font-semibold transition-colors disabled:opacity-60 ${
                            active
                                ? "bg-brightGreen text-white"
                                : "text-deepGreen hover:bg-cream"
                        }`}
                    >
                        <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
                        {switching === view.target ? "Switching…" : view.label}
                    </button>
                );
            })}
        </div>
    );
}
